import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Button, Message, Label, Icon } from 'semantic-ui-react'
import { DatePicker } from '../shared/DatePicker'
import WeekDaysSelect from '../shared/WeekDaysSelect'
import PropTypes from "prop-types";
import { onSuccess, onError, onWarning } from '../shared/messages'
import RatesDropDown from '../shared/RatesDropDown';
import RoomTypesDropDown from '../shared/RoomTypesDropDown';
import ProductsDropDown from '../shared/ProductsDropDown';
import { dateFormat, RateType } from '../shared/constants';
import { UpdateProductPricesByPercentOrAmount } from '../../api/services/pms';
import _ from 'lodash'
import { useTextResources } from '../../language';
import moment from 'moment';
import NumericInput from '../shared/NumericInput';
import { isBrowser, isMobileOnly } from 'mobile-device-detect'
import { useLoader } from '../shared/utils';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions } from '../shared/permissionsActions';


const PriceUpdatePopup = ({ onClose, onSave, HotelID, controller, startOpen, ...rest }) => {
    const text = useTextResources()
    const loader = useLoader()
    const [dates, setDates] = useState([new Date(), new Date()])
    const [open, setOpen] = useState(startOpen)
    const [days, setDays] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [hotelLevel, setHotelLevel] = useState(false)
    const [roomTypes, setRoomTypes] = useState([])
    const [rate, setRate] = useState(null)
    const [product, setProduct] = useState(null)
    const [rule, setRule] = useState(null)
    const [value, setValue] = useState("0")
    const [valueSingle, setValueSingle] = useState("0")
    const [valueDouble, setValueDouble] = useState("0")
    const [valuePerRoom, setValuePerRoom] = useState("0")
    const [params, setParams] = useState(null)
    const [rateItem, setRateItem] = useState(null)
    const [hotelId, setHotelId] = useState(HotelID)
    const [selectedDates, setSelectedDates] = useState([])

    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setDates([params.StartDate || new Date(), params.EndDate || new Date()])
                            setRate(params.RateID)
                            setRoomTypes(params.RoomTypeIds || [])
                            setProduct(params.ProductID ? { ID: params.ProductID } : null)
                            setHotelId(params.HotelId || HotelID)
                            setParams(params)
                        } else {
                            setDates([new Date(), new Date()])
                            setRate(null)
                            setRoomTypes([])
                            setProduct(null)
                            setParams(null)
                        }
                        setRule(null)
                        setValue("0")
                        setValueSingle("0")
                        setValueDouble("0")
                        setValuePerRoom("0")
                        setHotelLevel(false)
                        setOpen(true)
                        setRateItem(null)
                        setSelectedDates([])
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(() => {
        setProduct(null);
    }, [rateItem])

    useEffect(() => {
        setValue("0")
        setValueSingle("0")
        setValueDouble("0")
        setValuePerRoom("0")
    }, [rateItem, product, roomTypes, hotelLevel, rule])

    const ratePricingTypeId = useMemo(() => {
        return rateItem?.RatePricingElements.some(rpe => rpe.RatePricingTypeId === RateType.PerPerson) ?
            RateType.PerPerson :
            rateItem?.RatePricingElements[0].RatePricingTypeId;
    }, [rateItem]);

    const saveAction = async (close) => {
        setIsSaving(true)
        loader(true)
        let data = {}
        data[rule] = Math.abs(Number(value))
        switch (rule) {
            case "Percent":
                data["LiveTypeOfChangeOfPrice"] = (Number(value) >= 0) ? 1 : 2;
                break;
            case "Amount":
                data["LiveTypeOfChangeOfPrice"] = (Number(value) >= 0) ? 3 : 4;
                break;
            case "Fixed":
                data["LiveTypeOfChangeOfPrice"] = 5;
                if ((ratePricingTypeId && ratePricingTypeId == RateType.PerPerson) ||
                    (product && product.RatePricingTypeId == RateType.PerPerson)) {
                    data.PerPersonSinglePrice = valueSingle
                    data.PerPersonDoublePrice = valueDouble
                }
                else if ((ratePricingTypeId && ratePricingTypeId == RateType.PerRoom) ||
                    (product && product.RatePricingTypeId == RateType.PerRoom)) {
                    data.PerRoomPrice = valuePerRoom;
                }
                break;
        }
        try {
            const alldates = selectedDates.length ? selectedDates : [{
                startDate: dates[0],
                endDate: dates[1]
            }]
            const updated = await Promise.all(alldates.map(date => {
                return UpdateProductPricesByPercentOrAmount({
                    StartDate: date.startDate,
                    EndDate: date.endDate,
                    WeekDays: days,
                    RoomTypeIds: roomTypes,
                    RateId: rate || null,
                    ProductId: product ? [product.Id] : null,
                    HotelId: hotelId,
                    IsForcedHotelUpdate: hotelLevel,
                    IncludeBaseRatesOnly: true,
                    ...data
                })
            }))
            if (onSave) {
                onSave(updated)
            }
            if (params && params.OnSave) {
                params.OnSave(updated)
            }
            onSuccess()
            if (close) {
                setOpen(false)
                if (onClose) onClose()
            }
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        }
        loader(false)
        setIsSaving(false)
    }

    const closeAction = () => {
        setOpen(false)
        if (onClose) onClose()
    }

    const RoomTypesSpec = useMemo(() => {
        let spec = {
            hotelId
        }
        if (rate) {
            spec.RateIDs = rate
        }
        return spec
    }, [hotelId, rate])

    const RatesSpec = useMemo(() => {
        let spec = {
            hotelId,
            IncludeBaseRates: true,
            IncludeDayUse: false,
            IncludeRatePricingType: true,
            IsActive: true,
            FromDate: dates[0] ? dates[0].toISOString() : null,
            ToDate: dates[1] ? dates[1].toISOString() : null,
            ExcludeRateCategoryIDs: [8, 9],
            includes: ["RatePricingElements"]
        }
        if (!_.isEmpty(roomTypes)) {
            spec.RoomTypeIds = roomTypes
        }
        return spec
    }, [hotelId, roomTypes, dates[0], dates[1]])

    const ProductsSpec = useMemo(() => {
        let spec = {
            hotelId,
            StartDate: dates.length ? _.min(dates).toISOString() : null,
            EndDate: dates.length ? _.max(dates).toISOString() : null,
            AvoidAllRestriction: true,
            SkipChildren: true,
        }
        if (!_.isEmpty(roomTypes)) {
            spec.RoomTypeIds = roomTypes
        }
        if (rate) {
            spec.RateIds = rate
        }
        return spec
    }, [hotelId, roomTypes, rate, dates[0], dates[1]])

    const texts = {
        rule: rule && text(rule),
        SingleFixedPrice: text("SingleFixedPrice"),
        DoubleFixedPrice: text("DoubleFixedPrice"),
        ByPercent: text("ByPercent"),
        ByAmount: text("ByAmount"),
        BasePrice: text("BasePrice"),
        SetFixedPrice: text("SetFixedPrice"),
        RoomFixedPrice: text("RoomFixedPrice")
    }

    const priceOptions = () => {
        if ((rateItem || !_.isEmpty(product)) && !hotelLevel) {
            return [
                {
                    text: texts.ByPercent,
                    value: "Percent",
                },
                {
                    text: texts.ByAmount,
                    value: "Amount",
                },
                {
                    text: texts.SetFixedPrice,
                    value: "Fixed",
                }
            ]
        } else {
            return [
                {
                    text: texts.ByPercent,
                    value: "Percent",
                }
            ]
        }
    }

    const isSaveDisabled = () => {
        return isSaving || !rule || !(hotelLevel || !_.isEmpty(roomTypes) || rate || product)
    }

    const addDateToList = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(dates[0], 'day') && moment(d.endDate).isSame(dates[1], 'day'))) {
            onWarning(text('WarningDuplicateDates'))
            return
        }
        setSelectedDates([
            ...selectedDates,
            {
                startDate: dates[0],
                endDate: dates[1]
            }
        ])
    }

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue)
        } else {
            setDates([new Date(), new Date()])
        }
    }

    const onRemoveDateFromSelectedDates = async (index, e) => {
        if (e) {
            await setSelectedDates(selectedDates => selectedDates.filter((_, i) => i !== index))
        }
    }

    const resetFields = async () => {
        await setSelectedDates([])
        await setDates([new Date(), new Date()])
    }

    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate: dates[0], endDate: dates[1] }]
    }, [dates, selectedDates])

    const priceFields = () => {
        if (rule) {
            if (rule === "Fixed") {
                const getFields = () => {
                    const pricingTypeId = ratePricingTypeId || product?.RatePricingTypeId
                    const isVilla = rateItem?.IsVilla || product?.IsVilla
                    if (pricingTypeId === 1) {
                        return <Form.Field label={texts["RoomFixedPrice"]} control={NumericInput} min={0} max={9999999} onChange={setValuePerRoom} value={valuePerRoom} />
                    } else if (pricingTypeId === 2) {
                        if (isVilla) {
                            return <Form.Field label={texts["BasePrice"]} control={NumericInput} min={0} max={9999999} onChange={setValueDouble} value={valueDouble} />
                        } else {
                            return <>
                                <Form.Field label={texts["SingleFixedPrice"]} control={NumericInput} min={0} max={9999999} onChange={setValueSingle} value={valueSingle} />
                                <Form.Field label={texts["DoubleFixedPrice"]} control={NumericInput} min={0} max={9999999} onChange={setValueDouble} value={valueDouble} />
                            </>
                        }
                    }
                }
                return <Form.Group widths={(rateItem && (rateItem.IsVilla || ratePricingTypeId == RateType.PerRoom)) ? 1 : 2}>
                    {getFields()}
                </Form.Group>
            } else {
                return <Form.Field label={texts[rule]} control={NumericInput} min={rule === "Percent" ? -100 : -9999999} max={rule === "Percent" ? 100 : 9999999} digits={rule === "Percent" ? 0 : 2} onChange={setValue} value={value} />
            }
        }
        return null
    }

    return (
        <ValidateModalPermission open={open} onClose={onClose} actionId={PermissionsActions.AccessPricesPopup}>
            <Modal size={isMobileOnly ? "fullscreen" : "tiny"} className={isMobileOnly ? "mobile" : ""} open={open} style={{ zIndex: 1000000 }} {...rest} >
                <Modal.Header>{text("PricePopup")}</Modal.Header>
                <Modal.Content style={isMobileOnly ? { overflowY: "auto" } : { maxHeight: 615, overflowY: "auto" }}>
                    <Form size="small">
                        <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                            {selectedDates.map((selectedDate, index) => (
                                <Label.Group key={index}>
                                    <Label as="a">
                                        {moment(selectedDate.startDate).format(dateFormat)}
                                        -
                                        {moment(selectedDate.endDate).format(dateFormat)}
                                        <Icon key={index} name={"delete"} onClick={onRemoveDateFromSelectedDates.bind(this, index)} />
                                    </Label>
                                </Label.Group>
                            ))}
                        </Form.Group>
                        <Form.Group style={{ alignItems: "center" }}>
                            <Form.Field width={12} style={{ display: "grid", marginTop: "0.4em" }}>
                                <DatePicker
                                    type="range"
                                    onChange={handleDatesChanged}
                                    label={text("DateRange")}
                                    value={dates}
                                    minDate={moment().startOf("day").toDate()}
                                />
                            </Form.Field>
                            <Form.Field width={4} style={{alignSelf: 'end'}}>
                                <Button
                                    size="small"
                                    primary
                                    onClick={addDateToList}>{text("AddDate")}</Button>
                            </Form.Field>
                        </Form.Group>
                        <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates} />
                        <Form.Checkbox label={text("HotelLevel")} onChange={(e, { checked }) => {
                            setHotelLevel(checked);
                            setRule();
                            setRoomTypes([]);
                            setRate(null)
                            setProduct(null);
                        }} checked={hotelLevel} />
                        {
                            !hotelLevel && <React.Fragment>
                                <Form.Group widths={2} unstackable>
                                    <Form.Field label={text("RoomType")} clearable multiple search control={RoomTypesDropDown} spec={RoomTypesSpec} onChange={setRoomTypes} value={roomTypes} placeholder={text("All")} />
                                    <Form.Field label={text("Rate")} clearable search control={RatesDropDown} spec={RatesSpec} onChange={setRate} onChangeItem={setRateItem} value={rate} />
                                </Form.Group>
                                { !window.SelfieSettings && <Form.Field label={text("Product")} clearable search control={ProductsDropDown} descriptionField="RateName" spec={ProductsSpec} onChangeItem={setProduct} value={product ? product.Id : null} />}
                            </React.Fragment>
                        }
                        <Message warning size="small" visible>
                            {text("PriceOverridingAffects")}
                        </Message>
                        <Form.Select label={text("PriceRule")} options={priceOptions()} onChange={(e, { value }) => setRule(value)} />
                        {priceFields()}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {isBrowser && <Button disabled={isSaving} negativesize={isMobileOnly ? "tiny" : "small"} negative onClick={closeAction}>{text("Close")}</Button>}
                    {isBrowser && <Button size={"small"} onClick={resetFields}>{text("Reset")}</Button>}
                    {isBrowser && <Button disabled={isSaveDisabled()} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(true)}>{text("Saveandclose")}</Button>}
                    <Button disabled={isSaveDisabled()} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(false)}>{text("Save")}</Button>
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    )
}

PriceUpdatePopup.propTypes = {
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    HotelID: PropTypes.any,
    controller: PropTypes.string,
    startOpen: PropTypes.bool
}


export default PriceUpdatePopup